import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { api } from '../../context/AuthContext';
import { CapTableIntegrityPanel } from '../../components/admin/CapTableIntegrityPanel';
import { cn } from '../../lib/utils';
import { 
  ArrowLeft, Plus, Users, DollarSign, TrendingUp, Briefcase,
  ArrowRightLeft, History, PieChart, BarChart3, RefreshCw, Lock, LockOpen,
  User, Calendar, FileText, AlertTriangle, UserPlus, Mail, Phone, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCapTablePage() {
  const { instrumentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [instrument, setInstrument] = useState(null);
  const [capTable, setCapTable] = useState(null);
  const [events, setEvents] = useState([]);
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [parties, setParties] = useState([]);
  
  // Dialog states
  const [showMintDialog, setShowMintDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showAddInvestorDialog, setShowAddInvestorDialog] = useState(false);
  
  // Mint form (no offering_id - instrument IS the offering)
  const [mintForm, setMintForm] = useState({
    investor_id: '',
    units: '',
    price_per_unit: '',
    reason: '',
  });
  
  // Transfer form
  const [transferForm, setTransferForm] = useState({
    from_investor_id: '',
    to_investor_id: '',
    units: '',
    price_per_unit: '',
    reason: '',
  });
  
  // New investor form
  const [investorForm, setInvestorForm] = useState({
    party_type: 'investor_individual',
    display_name: '',
    legal_name: '',
    email: '',
    phone: '',
    tax_id: '',
    country_code: 'PT',
  });

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  // Bumped after anything that moves units, so the check reflects what just happened
  const [integrityKey, setIntegrityKey] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch instrument details (includes funding stats)
      const instrumentRes = await api.get(`/admin/projects/instruments/${instrumentId}`);
      setInstrument(instrumentRes.data);
      
      // Fetch cap table
      const capTableRes = await api.get(`/admin/v2/captable/securities/${instrumentId}/captable`).catch(() => null);
      if (capTableRes?.data) {
        setCapTable(capTableRes.data);
      }
      
      // Fetch event history
      const eventsRes = await api.get(`/admin/v2/captable/securities/${instrumentId}/events`).catch(() => ({ data: { events: [] } }));
      setEvents(eventsRes.data.events || []);
      
      // Fetch parties for investor selection
      const partiesRes = await api.get('/admin/v2/parties').catch(() => ({ data: { parties: [] } }));
      setParties(partiesRes.data.parties || []);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load cap table data');
    } finally {
      setLoading(false);
      setIntegrityKey((k) => k + 1);
    }
  }, [instrumentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleReopen = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/admin/projects/instruments/${instrumentId}/reopen`, {
        reason: 'Reopened for new subscriptions',
      });
      toast.success(
        res.data.changed?.length
          ? `${res.data.message} · ${res.data.units_available?.toLocaleString()} units available`
          : res.data.message
      );
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen instrument');
    } finally {
      setSaving(false);
    }
  };

  const handleMintUnits = async () => {
    if (!mintForm.investor_id) {
      toast.error('Please select an investor');
      return;
    }
    if (!mintForm.units || parseInt(mintForm.units) <= 0) {
      toast.error('Units must be greater than 0');
      return;
    }
    if (!mintForm.reason || mintForm.reason.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }

    setSaving(true);
    try {
      const result = await api.post('/admin/v2/captable/mint', {
        security_id: instrumentId,
        investor_id: mintForm.investor_id,
        units: parseInt(mintForm.units),
        price_per_unit: mintForm.price_per_unit || instrument?.unit_price || instrument?.unit_price_at_issuance || '1',
        reason: mintForm.reason,
      });
      
      let message = `Minted ${mintForm.units} units`;
      if (result.data?.subscription?.instrument_auto_closed) {
        message += ' - Instrument is now fully subscribed and closed';
      }
      toast.success(message);
      setShowMintDialog(false);
      setMintForm({ investor_id: '', units: '', price_per_unit: '', reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mint units');
    } finally {
      setSaving(false);
    }
  };

  const handleTransferUnits = async () => {
    if (!transferForm.from_investor_id) {
      toast.error('Please select sender');
      return;
    }
    if (!transferForm.to_investor_id) {
      toast.error('Please select recipient');
      return;
    }
    if (transferForm.from_investor_id === transferForm.to_investor_id) {
      toast.error('Sender and recipient must be different');
      return;
    }
    if (!transferForm.units || parseInt(transferForm.units) <= 0) {
      toast.error('Units must be greater than 0');
      return;
    }
    if (!transferForm.reason || transferForm.reason.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }

    setSaving(true);
    try {
      const res = await api.post('/admin/v2/captable/transfer', {
        security_id: instrumentId,
        from_investor_id: transferForm.from_investor_id,
        to_investor_id: transferForm.to_investor_id,
        units: parseInt(transferForm.units),
        price_per_unit: transferForm.price_per_unit || null,
        reason: transferForm.reason,
      });
      const moved = res.data?.investment_move?.amount;
      toast.success(
        moved
          ? `Transferred ${transferForm.units} units · €${Math.round(parseFloat(moved)).toLocaleString()} of investment moved across`
          : `Transferred ${transferForm.units} units`
      );
      setShowTransferDialog(false);
      setTransferForm({ from_investor_id: '', to_investor_id: '', units: '', price_per_unit: '', reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to transfer units');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (event) => {
    const label = event.event_type === 'transfer' ? 'transfer' : 'mint';
    const confirmed = window.confirm(
      `Delete this ${label} of ${event.units_delta?.toLocaleString()} units?\n\n` +
      `The units and the investment registered against them are put back as they were. ` +
      `This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingEventId(event.id);
    try {
      const res = await api.delete(`/admin/v2/captable/events/${event.id}`);
      toast.success(res.data?.message || 'Event deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to delete ${label}`);
    } finally {
      setDeletingEventId(null);
    }
  };

  const handleCreateInvestor = async () => {
    if (!investorForm.display_name.trim()) {
      toast.error('Display name is required');
      return;
    }
    if (!investorForm.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setSaving(true);
    try {
      const res = await api.post('/admin/v2/parties', {
        party_type: investorForm.party_type,
        display_name: investorForm.display_name,
        legal_name: investorForm.legal_name || investorForm.display_name,
        email: investorForm.email,
        phone: investorForm.phone || null,
        tax_id: investorForm.tax_id || null,
        country_code: investorForm.country_code,
        status: 'active',
        kyc_level: 'basic',
      });
      
      toast.success(`Investor "${investorForm.display_name}" created`);
      setShowAddInvestorDialog(false);
      setInvestorForm({
        party_type: 'investor_individual',
        display_name: '',
        legal_name: '',
        email: '',
        phone: '',
        tax_id: '',
        country_code: 'PT',
      });
      
      // Refresh parties list and auto-select the new investor in mint form
      fetchData();
      if (res.data?.party?.id) {
        setMintForm(prev => ({ ...prev, investor_id: res.data.party.id }));
        setShowMintDialog(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create investor');
    } finally {
      setSaving(false);
    }
  };

  // Get party name
  const getPartyName = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    return party?.display_name || party?.legal_name || partyId?.substring(0, 8) + '...';
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!instrument) {
    return (
      <div className="min-h-screen bg-background">
        <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Instrument Not Found</h2>
            <Button onClick={() => navigate('/admin/instruments')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Instruments
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const utilizationPercent = instrument.total_authorized_units > 0 
    ? ((instrument.units_outstanding || 0) / instrument.total_authorized_units) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-background">
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button & Title */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/instruments')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="h-6 w-px bg-border" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-foreground-muted">{instrument.code}</span>
              <Badge variant="outline" className="text-xs capitalize">{instrument.security_class}</Badge>
              <Badge className={cn(
                "text-xs",
                instrument.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              )}>
                {instrument.status || 'draft'}
              </Badge>
            </div>
            <h1 className="text-2xl font-bold mt-1">{instrument.name}</h1>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-white border border-black/[0.04]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Authorized</p>
                <p className="text-xl font-bold">{instrument.total_authorized_units?.toLocaleString() || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-black/[0.04]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Outstanding</p>
                <p className="text-xl font-bold">{instrument.units_outstanding?.toLocaleString() || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-black/[0.04]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Available</p>
                <p className="text-xl font-bold text-green-600">{instrument.units_available?.toLocaleString() || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-black/[0.04]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">Unit Price</p>
                <p className="text-xl font-bold">{instrument.currency} {instrument.unit_price_at_issuance}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Utilization Bar */}
        <Card className="p-4 bg-white border border-black/[0.04] mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Unit Utilization</span>
            <span className="text-sm text-foreground-muted">{utilizationPercent.toFixed(1)}% issued</span>
          </div>
          <Progress value={utilizationPercent} className="h-3" />
        </Card>

        {(() => {
          // Three separate things can stop an instrument taking money, so they are reported and
          // cleared together rather than one frustrating discovery at a time.
          const blockers = [];
          if (instrument?.is_frozen || instrument?.status === 'frozen') blockers.push('frozen');
          if (instrument?.status === 'draft') blockers.push('still in draft');
          if (instrument?.offering_status && instrument.offering_status !== 'open') {
            blockers.push(`offering ${instrument.offering_status}`);
          }
          if (instrument?.status === 'retired' || blockers.length === 0) return null;
          return (
            <div
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50/70 px-4 py-3 mb-6"
              data-testid="instrument-frozen-banner"
            >
              <div className="flex items-start gap-2.5 min-w-0">
                <Lock className="h-5 w-5 text-amber-600 flex-shrink-0" weight="duotone" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Not accepting new subscriptions or mints</p>
                  <p className="text-xs text-foreground-muted">
                    This instrument is {blockers.join(' · ')}
                    {instrument?.frozen_reason ? ` — ${instrument.frozen_reason}` : ''}
                  </p>
                </div>
              </div>
              <Button onClick={handleReopen} disabled={saving} data-testid="reopen-instrument-btn">
                <LockOpen className="h-4 w-4 mr-2" />
                Reopen for subscriptions
              </Button>
            </div>
          );
        })()}

        <CapTableIntegrityPanel instrumentId={instrumentId} refreshKey={integrityKey} />

        {/* Actions */}
        <div className="flex items-center gap-3 mb-8">
          <Button onClick={() => setShowMintDialog(true)} data-testid="mint-units-btn">
            <Plus className="h-4 w-4 mr-2" />
            Mint Units
          </Button>
          <Button variant="outline" onClick={() => setShowTransferDialog(true)} data-testid="transfer-units-btn">
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Transfer Units
          </Button>
          <Button variant="outline" onClick={() => setShowAddInvestorDialog(true)} data-testid="add-investor-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Add Investor
          </Button>
          <Button variant="ghost" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Ownership Positions */}
          <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <PieChart className="h-4 w-4" />
              Ownership Positions
            </h3>
            
            {(!capTable?.positions || capTable.positions.length === 0) ? (
              <div className="text-center py-8">
                <Users className="h-10 w-10 text-foreground-muted mx-auto mb-3" />
                <p className="text-foreground-muted text-sm">No positions yet</p>
                <p className="text-foreground-muted text-xs mt-1">Mint units to investors to create positions</p>
              </div>
            ) : (
              <div className="space-y-3">
                {capTable.positions.map((pos, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/investor/${pos.investor_id}`)}
                    data-testid={`position-${pos.investor_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-primary hover:underline">{getPartyName(pos.investor_id)}</p>
                        <p className="text-xs text-foreground-muted">
                          {Number(pos.percent_of_outstanding || 0).toFixed(2)}% of outstanding
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{pos.units_held?.toLocaleString() || 0}</p>
                      <p className="text-xs text-foreground-muted">units</p>
                      <p className="text-xs font-medium mt-1" data-testid={`position-invested-${pos.investor_id}`}>
                        €{Math.round(parseFloat(pos.invested_amount || 0)).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-foreground-muted">invested</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Event History */}
          <Card className="bg-white p-6 rounded-xl border border-black/[0.04]">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <History className="h-4 w-4" />
              Event History
            </h3>
            
            {events.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-10 w-10 text-foreground-muted mx-auto mb-3" />
                <p className="text-foreground-muted text-sm">No events yet</p>
                <p className="text-foreground-muted text-xs mt-1">Cap table events will appear here</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {events.slice(0, 20).map((event, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                      event.event_type === 'mint' ? 'bg-green-100' :
                      event.event_type === 'transfer' ? 'bg-blue-100' :
                      event.event_type === 'redemption' ? 'bg-amber-100' :
                      'bg-gray-100'
                    )}>
                      {event.event_type === 'mint' ? <Plus className="h-4 w-4 text-green-600" /> :
                       event.event_type === 'transfer' ? <ArrowRightLeft className="h-4 w-4 text-blue-600" /> :
                       <FileText className="h-4 w-4 text-gray-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs capitalize">{event.event_type}</Badge>
                        <span className="text-xs text-foreground-muted">
                          {event.effective_date ? new Date(event.effective_date).toLocaleDateString() : ''}
                        </span>
                      </div>
                      <p className="text-sm mt-1">
                        {event.units_delta?.toLocaleString()} units
                        {event.event_type === 'transfer' && event.counterparty_id && (
                          <span className="text-foreground-muted">
                            {' '}→ {getPartyName(event.counterparty_id)}
                          </span>
                        )}
                      </p>
                      {event.reason && (
                        <p className="text-xs text-foreground-muted mt-1 truncate">{event.reason}</p>
                      )}
                    </div>
                    {['mint', 'transfer'].includes(event.event_type) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-foreground-muted hover:text-red-600"
                        disabled={deletingEventId === event.id}
                        onClick={() => handleDeleteEvent(event)}
                        data-testid={`delete-event-${event.id}`}
                        title={`Delete this ${event.event_type}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </main>

      {/* ================================================================== */}
      {/* MINT DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showMintDialog} onOpenChange={setShowMintDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Mint Units</DialogTitle>
            <DialogDescription>
              Issue new units to an investor. Creates a subscription and updates the instrument's funding progress.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Instrument funding status */}
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Available to mint:</span>
                <span className="font-semibold">{(instrument?.units_available || 0).toLocaleString()} units</span>
              </div>
              <div className="flex justify-between text-sm text-foreground-muted">
                <span>Subscribed:</span>
                <span>{(instrument?.units_subscribed || 0).toLocaleString()} / {(instrument?.total_authorized_units || 0).toLocaleString()}</span>
              </div>
              {instrument?.offering_status === 'closed' && (
                <p className="text-xs text-red-600 mt-2">⚠️ This instrument is closed for new subscriptions</p>
              )}
            </div>
            
            <div>
              <Label>Investor *</Label>
              <Select value={mintForm.investor_id} onValueChange={(v) => setMintForm({ ...mintForm, investor_id: v })}>
                <SelectTrigger className="mt-1" data-testid="mint-investor-select">
                  <SelectValue placeholder="Select investor..." />
                </SelectTrigger>
                <SelectContent>
                  {parties.filter(p => p.party_type?.startsWith('investor_')).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.display_name || p.legal_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Units to Mint *</Label>
                <Input 
                  type="number"
                  value={mintForm.units}
                  onChange={(e) => setMintForm({ ...mintForm, units: e.target.value })}
                  placeholder="e.g., 1000"
                  className="mt-1"
                  data-testid="mint-units-input"
                />
              </div>
              <div>
                <Label>Price per Unit</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={mintForm.price_per_unit}
                  onChange={(e) => setMintForm({ ...mintForm, price_per_unit: e.target.value })}
                  placeholder={instrument?.unit_price || instrument?.unit_price_at_issuance || '1'}
                  className="mt-1"
                  data-testid="mint-price-input"
                />
                <p className="text-xs text-foreground-muted mt-1">Defaults to instrument price if empty</p>
              </div>
            </div>
            
            <div>
              <Label>Reason *</Label>
              <Textarea 
                value={mintForm.reason}
                onChange={(e) => setMintForm({ ...mintForm, reason: e.target.value })}
                placeholder="e.g., Manual allocation for investor subscription"
                rows={2}
                className="mt-1"
                data-testid="mint-reason-input"
              />
            </div>

            {instrument?.units_available > 0 && (
              <p className="text-sm text-foreground-muted">
                Available to mint: {instrument.units_available.toLocaleString()} units
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMintDialog(false)}>Cancel</Button>
            <Button onClick={handleMintUnits} disabled={saving} data-testid="confirm-mint-btn">
              {saving ? 'Minting...' : 'Mint Units'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* TRANSFER DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Transfer Units</DialogTitle>
            <DialogDescription>
              Transfer units from one investor to another.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>From Investor *</Label>
              <Select value={transferForm.from_investor_id} onValueChange={(v) => setTransferForm({ ...transferForm, from_investor_id: v })}>
                <SelectTrigger className="mt-1" data-testid="transfer-from-select">
                  <SelectValue placeholder="Select sender..." />
                </SelectTrigger>
                <SelectContent>
                  {capTable?.positions?.map(pos => (
                    <SelectItem key={pos.investor_id} value={pos.investor_id}>
                      {getPartyName(pos.investor_id)} ({pos.units_held?.toLocaleString()} units)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>To Investor *</Label>
              <Select value={transferForm.to_investor_id} onValueChange={(v) => setTransferForm({ ...transferForm, to_investor_id: v })}>
                <SelectTrigger className="mt-1" data-testid="transfer-to-select">
                  <SelectValue placeholder="Select recipient..." />
                </SelectTrigger>
                <SelectContent>
                  {parties.filter(p => p.party_type?.startsWith('investor_')).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.display_name || p.legal_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Units to Transfer *</Label>
                <Input 
                  type="number"
                  value={transferForm.units}
                  onChange={(e) => setTransferForm({ ...transferForm, units: e.target.value })}
                  placeholder="e.g., 500"
                  className="mt-1"
                  data-testid="transfer-units-input"
                />
              </div>
              <div>
                <Label>Price per Unit (optional)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={transferForm.price_per_unit}
                  onChange={(e) => setTransferForm({ ...transferForm, price_per_unit: e.target.value })}
                  placeholder="Market price"
                  className="mt-1"
                  data-testid="transfer-price-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Reason *</Label>
              <Textarea 
                value={transferForm.reason}
                onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })}
                placeholder="e.g., Secondary market sale"
                rows={2}
                className="mt-1"
                data-testid="transfer-reason-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
            <Button onClick={handleTransferUnits} disabled={saving} data-testid="confirm-transfer-btn">
              {saving ? 'Transferring...' : 'Transfer Units'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* ADD INVESTOR DIALOG */}
      {/* ================================================================== */}
      <Dialog open={showAddInvestorDialog} onOpenChange={setShowAddInvestorDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Add New Investor
            </DialogTitle>
            <DialogDescription>
              Create a new investor/party to mint units to. They will appear in the investor selection.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Investor Type</Label>
              <Select value={investorForm.party_type} onValueChange={(v) => setInvestorForm({ ...investorForm, party_type: v })}>
                <SelectTrigger className="mt-1" data-testid="investor-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="investor_individual">Individual Investor</SelectItem>
                  <SelectItem value="investor_institutional">Institutional Investor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Display Name *</Label>
              <Input 
                value={investorForm.display_name}
                onChange={(e) => setInvestorForm({ ...investorForm, display_name: e.target.value })}
                placeholder={investorForm.party_type === 'investor_individual' ? 'e.g., John Smith' : 'e.g., ABC Investments Ltd'}
                className="mt-1"
                data-testid="investor-name-input"
              />
            </div>
            
            <div>
              <Label>Legal Name</Label>
              <Input 
                value={investorForm.legal_name}
                onChange={(e) => setInvestorForm({ ...investorForm, legal_name: e.target.value })}
                placeholder="Full legal name (if different)"
                className="mt-1"
                data-testid="investor-legal-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
                  <Input 
                    type="email"
                    value={investorForm.email}
                    onChange={(e) => setInvestorForm({ ...investorForm, email: e.target.value })}
                    placeholder="investor@email.com"
                    className="pl-9"
                    data-testid="investor-email-input"
                  />
                </div>
              </div>
              <div>
                <Label>Phone</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
                  <Input 
                    type="tel"
                    value={investorForm.phone}
                    onChange={(e) => setInvestorForm({ ...investorForm, phone: e.target.value })}
                    placeholder="+351 912 345 678"
                    className="pl-9"
                    data-testid="investor-phone-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tax ID / NIF</Label>
                <Input 
                  value={investorForm.tax_id}
                  onChange={(e) => setInvestorForm({ ...investorForm, tax_id: e.target.value })}
                  placeholder="e.g., 123456789"
                  className="mt-1"
                  data-testid="investor-tax-id-input"
                />
              </div>
              <div>
                <Label>Country</Label>
                <Select value={investorForm.country_code} onValueChange={(v) => setInvestorForm({ ...investorForm, country_code: v })}>
                  <SelectTrigger className="mt-1" data-testid="investor-country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PT">Portugal</SelectItem>
                    <SelectItem value="ES">Spain</SelectItem>
                    <SelectItem value="FR">France</SelectItem>
                    <SelectItem value="DE">Germany</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddInvestorDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateInvestor} disabled={saving} data-testid="create-investor-btn">
              {saving ? 'Creating...' : 'Create & Proceed to Mint'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
